import { Box, ButtonBase, Typography } from "@mui/material";
import { alpha, styled } from "@mui/material/styles";

interface IEstadoNavegacao {
    ativo?: boolean;
    compacto?: boolean;
    comGuia?: boolean;
}

const naoRepassar = (prop: PropertyKey) =>
    prop !== "ativo" && prop !== "compacto" && prop !== "comGuia";

export const AreaRolagem = styled(Box)(({ theme }) => ({
    flex: 1,
    minHeight: 0,
    overflowY: "auto",
    overflowX: "hidden",
    padding: theme.spacing(0.5, 1.25, 1.5),
    "&::-webkit-scrollbar": { width: 6 },
    "&::-webkit-scrollbar-track": { background: "transparent" },
    "&::-webkit-scrollbar-thumb": {
        borderRadius: 8,
        backgroundColor: alpha(theme.palette.text.secondary, 0.22),
    },
    "&:hover::-webkit-scrollbar-thumb": {
        backgroundColor: alpha(theme.palette.text.secondary, 0.38),
    },
}));

export const RotuloGrupo = styled(Typography)(({ theme }) => ({
    display: "block",
    padding: theme.spacing(0, 1.5),
    margin: theme.spacing(2.25, 0, 0.75),
    fontSize: "0.625rem",
    fontWeight: 700,
    letterSpacing: "0.09em",
    textTransform: "uppercase",
    color: alpha(theme.palette.text.secondary, 0.85),
    whiteSpace: "nowrap",
}));

export const BotaoModulo = styled(ButtonBase, { shouldForwardProp: naoRepassar })<IEstadoNavegacao>(
    ({ theme, ativo, compacto }) => ({
        position: "relative",
        width: "100%",
        height: 42,
        display: "flex",
        alignItems: "center",
        justifyContent: compacto ? "center" : "flex-start",
        gap: theme.spacing(1.5),
        padding: compacto ? 0 : theme.spacing(0, 1.5),
        borderRadius: 12,
        textAlign: "left",
        color: ativo ? theme.palette.primary.main : theme.palette.text.primary,
        backgroundColor: ativo ? alpha(theme.palette.primary.main, 0.1) : "transparent",
        transition: theme.transitions.create(["background-color", "color"], { duration: 160 }),
        "&:hover": {
            backgroundColor: ativo
                ? alpha(theme.palette.primary.main, 0.16)
                : alpha(theme.palette.text.primary, 0.06),
        },
        ...(ativo && compacto
            ? {
                "&::before": {
                    content: '""',
                    position: "absolute",
                    left: -10,
                    top: "50%",
                    transform: "translateY(-50%)",
                    width: 3,
                    height: 20,
                    borderRadius: 4,
                    backgroundColor: theme.palette.primary.main,
                },
            }
            : {}),
    })
);

export const BotaoItem = styled(ButtonBase, { shouldForwardProp: naoRepassar })<IEstadoNavegacao>(
    ({ theme, ativo, comGuia }) => ({
        position: "relative",
        width: "100%",
        height: 34,
        display: "flex",
        alignItems: "center",
        justifyContent: "flex-start",
        gap: theme.spacing(1),
        padding: theme.spacing(0, 1.25),
        borderRadius: 8,
        textAlign: "left",
        fontSize: "0.8rem",
        color: ativo ? theme.palette.primary.main : theme.palette.text.secondary,
        backgroundColor: ativo ? alpha(theme.palette.primary.main, 0.1) : "transparent",
        transition: theme.transitions.create(["background-color", "color"], { duration: 160 }),
        "&:hover": {
            color: ativo ? theme.palette.primary.main : theme.palette.text.primary,
            backgroundColor: ativo
                ? alpha(theme.palette.primary.main, 0.16)
                : alpha(theme.palette.text.primary, 0.05),
        },
        // O item ativo acende o trecho correspondente da linha-guia do módulo.
        ...(ativo && comGuia
            ? {
                "&::before": {
                    content: '""',
                    position: "absolute",
                    left: -11,
                    top: 4,
                    bottom: 4,
                    width: 2,
                    borderRadius: 2,
                    backgroundColor: theme.palette.primary.main,
                },
            }
            : {}),
    })
);

/** Linha-guia vertical que amarra os itens ao módulo pai. */
export const ListaItens = styled(Box)(({ theme }) => ({
    display: "flex",
    flexDirection: "column",
    gap: 2,
    margin: theme.spacing(0.25, 0, 0.5, 2.5),
    paddingLeft: theme.spacing(1.25),
    borderLeft: `1px solid ${theme.palette.divider}`,
}));

export const CaixaBusca = styled(Box)(({ theme }) => ({
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    height: 38,
    padding: theme.spacing(0, 1.25),
    borderRadius: 10,
    backgroundColor: alpha(theme.palette.text.primary, 0.045),
    border: `1px solid transparent`,
    transition: theme.transitions.create(["border-color", "background-color"], { duration: 160 }),
    "&:focus-within": {
        borderColor: alpha(theme.palette.primary.main, 0.45),
        backgroundColor: alpha(theme.palette.primary.main, 0.05),
    },
}));

/** Badge numérico de notificações exibido dentro dos botões do menu. */
export const Contador = styled(Box)(({ theme }) => ({
    flexShrink: 0,
    minWidth: 20,
    height: 18,
    padding: theme.spacing(0, 0.625),
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    borderRadius: 9,
    fontSize: "0.6875rem",
    fontWeight: 700,
    lineHeight: 1,
    letterSpacing: 0,
    backgroundColor: theme.palette.error.main,
    color: theme.palette.error.contrastText,
}));

export const Rodape = styled(Box)(({ theme }) => ({
    borderTop: `1px solid ${theme.palette.divider}`,
    padding: theme.spacing(1, 1.25),
}));
